import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaPlay, FaEye, FaClock, FaChevronLeft, FaTimes, FaSearch } from 'react-icons/fa';
import CustomerHeader from '../components/CustomerHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import CustomAlert from '../components/CustomAlert';
import trainingVideoService, { TrainingVideo, CategoryInfo } from '../services/trainingVideoService';
import { MobileContainer } from '../theme/GlobalStyles';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled(MobileContainer)`
  padding-top: 1rem;
  padding-bottom: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const BackButton = styled.button`
  background: ${props => props.theme.surface};
  border: 2px solid ${props => props.theme.border};
  color: ${props => props.theme.textPrimary};
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.primary};
  margin: 0;
  font-size: 1.5rem;
  flex: 1;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 2px solid ${props => props.theme.border};
  border-radius: 25px;
  font-size: 1rem;
  transition: all 0.2s;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px rgba(244, 0, 0, 0.1);
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.textSecondary};
  pointer-events: none;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.surface};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.primary};
    border-radius: 4px;
  }
`;

const CategoryTab = styled.button<{ isActive: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.isActive ? props.theme.primary : props.theme.border};
  background: ${props => props.isActive ? props.theme.primary : 'white'};
  color: ${props => props.isActive ? 'white' : props.theme.textPrimary};
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.isActive ? props.theme.primaryDark : props.theme.surface};
  }
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(244, 0, 0, 0.2);
  }
`;

const VideoThumbnail = styled.div<{ thumbnailUrl?: string }>`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background: ${props => props.thumbnailUrl 
    ? `url(${props.thumbnailUrl}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  overflow: hidden;
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  transition: background 0.3s;
  
  ${VideoCard}:hover & {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const PlayIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.theme.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  transition: transform 0.3s;
  
  ${VideoCard}:hover & {
    transform: scale(1.1);
  }
`;

const VideoInfo = styled.div`
  padding: 1rem;
`;

const VideoTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
`;

const VideoDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0 0 0.75rem 0;
  font-size: 0.85rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VideoMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${props => props.theme.textSecondary};
  
  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.3;
  }
  
  h3 {
    color: ${props => props.theme.textPrimary};
    margin: 0 0 0.5rem 0;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const VideoPlayerModal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const VideoPlayerContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
`;

const VideoPlayerHeader = styled.div`
  background: #1a1a1a;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const VideoPlayerTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.1rem;
  flex: 1;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-height: 70vh;
  background: #000;
`;

const VideoPlayerInfo = styled.div`
  background: #1a1a1a;
  padding: 1rem;
  color: white;
`;

const VideoPlayerDescription = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #ccc;
`;

const VideoPlayerMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  font-size: 0.85rem;
  color: #999;
`;

const TrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedVideo, setSelectedVideo] = useState<TrainingVideo | null>(null);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message: string;
  }>({ isVisible: false, message: '' });

  useEffect(() => {
    const initializePage = async () => {
      await loadCategories();
      await loadVideos();
    };
    initializePage();
  }, []);

  useEffect(() => {
    if (selectedCategory && !loading) {
      loadVideos(selectedCategory === 'All' ? undefined : selectedCategory);
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await trainingVideoService.getCategories();
      console.log('Categories response:', response);
      
      if (response.success && response.data && response.data.length > 0) {
        const allCount = response.data.reduce((sum: number, cat: any) => sum + (cat.videoCount || cat.VideoCount || 0), 0);
        const mappedCategories = response.data.map((cat: any) => ({
          Category: cat.category || cat.Category,
          VideoCount: cat.videoCount || cat.VideoCount || 0
        }));
        
        setCategories([
          { Category: 'All', VideoCount: allCount },
          ...mappedCategories
        ]);
      } else {
        // If no categories, just show All with 0 count
        setCategories([{ Category: 'All', VideoCount: 0 }]);
      }
    } catch (error: any) {
      console.error('Error loading categories:', error);
      // Show All category even on error
      setCategories([{ Category: 'All', VideoCount: 0 }]);
    }
  };

  const loadVideos = async (category?: string) => {
    try {
      setLoading(true);
      const response = await trainingVideoService.getVideos(category, true);
      
      if (response.success && response.data) {
        setVideos(response.data);
      } else {
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Error',
          message: response.message || 'Failed to load training videos'
        });
      }
    } catch (error: any) {
      console.error('Error loading videos:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to load training videos'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = async (video: TrainingVideo) => {
    setSelectedVideo(video);
    setIsPlayerVisible(true);
    
    // Increment view count
    await trainingVideoService.incrementViewCount(video.uid);
  };

  const handleClosePlayer = () => {
    setIsPlayerVisible(false);
    setSelectedVideo(null);
  };

  // Filter videos based on search query
  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) {
      return videos;
    }
    
    const query = searchQuery.toLowerCase();
    return videos.filter(video => 
      video.title.toLowerCase().includes(query) ||
      video.description?.toLowerCase().includes(query) ||
      video.category.toLowerCase().includes(query)
    );
  }, [videos, searchQuery]);

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} />
      <ContentContainer>
        <PageHeader>
          <BackButton onClick={() => navigate('/dashboard')}>
            {React.createElement(FaChevronLeft as any, {})}
          </BackButton>
          <PageTitle>Customer Training Videos</PageTitle>
        </PageHeader>

        <SearchContainer>
          <SearchIcon>
            {React.createElement(FaSearch as any, {})}
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search videos by title, description, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        {categories.length > 0 && (
          <CategoryTabs>
            {categories.map((category) => (
              <CategoryTab
                key={category.Category}
                isActive={selectedCategory === category.Category}
                onClick={() => setSelectedCategory(category.Category)}
              >
                {category.Category}
                <span style={{ opacity: 0.7 }}>({category.VideoCount})</span>
              </CategoryTab>
            ))}
          </CategoryTabs>
        )}

        {loading ? (
          <LoadingSpinner isVisible={loading} />
        ) : filteredVideos.length === 0 ? (
          <EmptyState>
            {React.createElement(FaSearch as any, {})}
            <h3>{searchQuery ? 'No Videos Found' : 'No Videos Available'}</h3>
            <p>{searchQuery ? 'Try a different search term' : 'There are no training videos in this category yet.'}</p>
          </EmptyState>
        ) : (
          <VideosGrid>
            {filteredVideos.map((video) => {
              const thumbnailUrl = video.thumbnailPath 
                ? trainingVideoService.getVideoFileUrl(video.thumbnailPath)
                : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360"%3E%3Crect fill="%23f5f5f5" width="640" height="360"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23666" font-size="48"%3E🎥%3C/text%3E%3C/svg%3E';
              
              return (
                <VideoCard key={video.uid} onClick={() => handleVideoClick(video)}>
                  <VideoThumbnail thumbnailUrl={thumbnailUrl}>
                    <PlayOverlay>
                      <PlayIcon>
                        {React.createElement(FaPlay as any, {})}
                      </PlayIcon>
                    </PlayOverlay>
                  </VideoThumbnail>
                <VideoInfo>
                  <VideoTitle>{video.title}</VideoTitle>
                  {video.description && (
                    <VideoDescription>{video.description}</VideoDescription>
                  )}
                  <VideoMeta>
                    <MetaItem>
                      {React.createElement(FaClock as any, {})}
                      {trainingVideoService.formatDuration(video.duration)}
                    </MetaItem>
                    <MetaItem>
                      {React.createElement(FaEye as any, {})}
                      {video.viewCount} views
                    </MetaItem>
                  </VideoMeta>
                </VideoInfo>
              </VideoCard>
              );
            })}
          </VideosGrid>
        )}
      </ContentContainer>

      <VideoPlayerModal isVisible={isPlayerVisible} onClick={handleClosePlayer}>
        {selectedVideo && (
          <VideoPlayerContainer onClick={(e) => e.stopPropagation()}>
            <VideoPlayerHeader>
              <VideoPlayerTitle>{selectedVideo.title}</VideoPlayerTitle>
              <CloseButton onClick={handleClosePlayer}>
                {React.createElement(FaTimes as any, {})}
              </CloseButton>
            </VideoPlayerHeader>
            <VideoPlayer
              controls
              autoPlay
              src={trainingVideoService.getVideoStreamUrl(selectedVideo.uid)}
            >
              Your browser does not support the video tag.
            </VideoPlayer>
            <VideoPlayerInfo>
              {selectedVideo.description && (
                <VideoPlayerDescription>{selectedVideo.description}</VideoPlayerDescription>
              )}
              <VideoPlayerMeta>
                <div>Category: {selectedVideo.category}</div>
                <div>Duration: {trainingVideoService.formatDuration(selectedVideo.duration)}</div>
                <div>Views: {selectedVideo.viewCount}</div>
                <div>Size: {selectedVideo.fileSizeMB.toFixed(1)} MB</div>
              </VideoPlayerMeta>
            </VideoPlayerInfo>
          </VideoPlayerContainer>
        )}
      </VideoPlayerModal>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
      />
    </PageContainer>
  );
};

export default TrainingPage;

